import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import LandingNav from '../components/landing/LandingNav';
import SiteFooter from '../components/landing/SiteFooter';
import BsiqActionButton from '../components/landing/BsiqActionButton';
import { openCalendly } from '../lib/calendly';
import { openApp } from '../lib/siteUrls';
import {
  Radar, ShieldCheck, BellRing,
  MessageSquare, UserCheck, LineChart,
  FileText, RefreshCw, Eye,
} from 'lucide-react';

const CREAM = '#F5F1EC';
const NAVY = '#0D1F3C';
const GOLD = '#C49A00';
const GOLD_BG = '#FEF3C7';
const SKY = '#5BA9E6';
const SKY_PALE = '#EAF5FC';
const BORDER = '#E5E7EB';
const TEXT_MUTED = '#4B5563';
const TEXT_LIGHT = '#6B7280';
const CARD_BORDER = '#F3F4F6';
/* ============================================================
   LiveFeed component — SAM.gov feed with 3 anchor cards + fallback
   ============================================================ */
const ANCHORS = [
  {
    key: 'a1', kind: 'forecast',
    agency: 'Department of Veterans Affairs',
    title: 'Healthcare Staffing Support — VISN 8 Florida Network',
    naics: '561320',
    stage: { label: 'FORECAST', bg: '#0D1F3C', color: '#FFFFFF' },
    evidence: 'Profile review',
    sub: 'Projected Q4 Release', time: 'Forecasted',
    deadline: null,
  },
  {
    key: 'a2', kind: 'active',
    agency: 'Dept of Health & Human Services',
    title: 'Registered Nurse Staffing — IHS Phoenix Area Office',
    naics: '561320',
    stage: { label: 'ACTIVE', bg: '#16A34A', color: '#FFFFFF' },
    evidence: 'Eligibility review',
    sub: null, time: 'Posted 3d ago',
    deadline: 'DUE AUG 10',
  },
  {
    key: 'a3', kind: 'ss',
    agency: 'Defense Health Agency',
    title: 'Allied Health Professional Staffing — Sources Sought',
    naics: '561320',
    stage: { label: 'SS/RFI', bg: '#64748B', color: '#FFFFFF' },
    evidence: 'Market signal',
    sub: 'Pipeline Signal — Track for RFP', time: 'Posted 1d ago',
    deadline: null,
  },
];

const FALLBACK_OPPS = [
  { agency: 'Department of Defense', title: 'Cybersecurity Support Services — DISA HQ', naics: '541512' },
  { agency: 'General Services Administration', title: 'IT Modernization Advisory — GSA IT Cat', naics: '541511' },
  { agency: 'Department of Homeland Security', title: 'Border Enforcement Analytics Platform', naics: '541990' },
  { agency: 'US Army Corps of Engineers', title: 'Environmental Remediation — Great Lakes District', naics: '562910' },
  { agency: 'Department of Energy', title: 'National Lab Facilities Management O&M', naics: '561210' },
  { agency: 'NASA Goddard Space Flight Center', title: 'Ground Systems Integration Support', naics: '541330' },
  { agency: 'Department of Justice', title: 'FBI Regional Data Center Operations', naics: '541519' },
  { agency: 'Social Security Administration', title: 'Legacy System Modernization', naics: '541511' },
  { agency: 'Department of Transportation', title: 'FAA NextGen Air Traffic Systems', naics: '541330' },
  { agency: 'Department of the Treasury', title: 'IRS Taxpayer Services Contact Center', naics: '561422' },
  { agency: 'Department of Commerce', title: 'NIST Cybersecurity Framework Consulting', naics: '541611' },
  { agency: 'US Coast Guard', title: 'Vessel Maintenance & Overhaul — Atlantic Fleet', naics: '336611' },
  { agency: 'Department of Agriculture', title: 'FSIS Inspection Software Development', naics: '541511' },
  { agency: 'Department of the Interior', title: 'BLM Wildfire Response Coordination', naics: '561210' },
  { agency: 'Environmental Protection Agency', title: 'Superfund Site Assessment Services', naics: '541620' },
  { agency: 'US Postal Service', title: 'Fleet Electrification Program Management', naics: '541611' },
];

const STAGES = ['FORECAST', 'ACTIVE', 'SS/RFI', 'QUALIFY'];
const pickStage = (index) => STAGES[index % STAGES.length];
const stageStyle = (s) => ({
  FORECAST: { bg: '#0D1F3C', color: '#FFFFFF' },
  ACTIVE:   { bg: '#16A34A', color: '#FFFFFF' },
  'SS/RFI': { bg: '#64748B', color: '#FFFFFF' },
  QUALIFY:  { bg: '#2563EB', color: '#FFFFFF' },
}[s]);

const LiveFeed = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // The public landing page uses representative rows. Authenticated live
      // opportunity views are backed by GovCon through the BSIQ API.
      let extra = FALLBACK_OPPS.map((o, i) => ({
          key: 'fb-' + i,
          agency: o.agency, title: o.title, naics: o.naics,
          stage: pickStage(i),
          evidence: i % 3 === 0 ? 'Source verified' : i % 3 === 1 ? 'Evidence review' : 'New signal',
          sub: null,
          time: `Posted ${(i % 9) + 1}d ago`,
          deadline: null,
        }));

      // Normalize non-anchor rows so stage is object w/ style
      extra = extra.map((r) => {
        const stg = typeof r.stage === 'string' ? r.stage : (r.stage?.label || 'ACTIVE');
        return { ...r, stage: { label: stg, ...stageStyle(stg) } };
      });

      if (!cancelled) {
        setRows([...ANCHORS, ...extra]);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const doubled = [...rows, ...rows]; // for seamless loop

  return (
    <div style={{ width: '100%' }}>
      <div
        className="relative"
        style={{
          width: '100%',
          height: '520px',
          backgroundColor: '#FFFFFF',
          border: `1px solid ${BORDER}`,
          borderRadius: '16px',
          boxShadow: '0 20px 40px -12px rgba(13,17,23,0.10), 0 6px 12px -4px rgba(13,17,23,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        data-testid="live-feed"
      >
        {/* Header — sticky/pinned at top of the flex column, never overlapped */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{
            borderBottom: `1px solid ${BORDER}`,
            backgroundColor: '#FFFFFF',
            flexShrink: 0,
            position: 'relative',
            zIndex: 20,
          }}
          data-testid="live-feed-header"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bsiq-pulse" style={{ backgroundColor: '#16A34A' }}></span>
            <span className="text-xs font-semibold" style={{ letterSpacing: '0.08em', color: NAVY }}>OPPORTUNITY SIGNALS</span>
          </div>
          <span className="text-[11px]" style={{ color: TEXT_LIGHT }}>Powered by BidsignalIQ</span>
        </div>

        {/* Scroll region — takes remaining flex space, clips its own overflow */}
        <div
          className="relative"
          style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
        >
          {/* fade masks */}
          <div className="absolute top-0 left-0 right-0 pointer-events-none z-10" style={{ height: '60px', background: 'linear-gradient(to bottom, #FFFFFF 0%, rgba(255,255,255,0) 100%)' }}></div>
          <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-10" style={{ height: '60px', background: 'linear-gradient(to top, #FFFFFF 0%, rgba(255,255,255,0) 100%)' }}></div>

          {loading && (
            <div className="p-4 space-y-2">
              {[0,1,2,3].map((i) => (
                <div key={i} className="rounded-lg p-4 bsiq-shimmer" style={{ height: '92px' }}></div>
              ))}
            </div>
          )}

          {!loading && (
            <div className="bsiq-feed-scroll">
              <div className="px-4 py-4 space-y-2">
                {doubled.map((r, idx) => (
                  <FeedCard key={r.key + '-' + idx} row={r} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="mt-4 text-sm text-center" style={{ color: TEXT_MUTED }}>
        <span className="font-semibold" style={{ color: NAVY }}>Representative workflow</span> · authenticated results are organization-specific
      </p>
    </div>
  );
};

const FeedCard = ({ row }) => {
  return (
    <div className="rounded-[10px] relative"
      style={{ backgroundColor: '#FFFFFF', border: `1px solid ${CARD_BORDER}`, padding: '16px' }}>
      {row.deadline && (
        <span className="absolute top-3 right-3 text-[11px] font-bold px-2 py-0.5 rounded"
          style={{ backgroundColor: GOLD_BG, color: '#B45309' }}>
          {row.deadline}
        </span>
      )}
      <p className="text-[11px] uppercase font-medium tracking-wide" style={{ color: TEXT_LIGHT }}>{row.agency}</p>
      <p className="text-sm font-semibold mt-1 leading-snug" style={{ color: NAVY, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {row.title}
      </p>
      {row.sub && <p className="text-[11px] mt-1" style={{ color: TEXT_LIGHT }}>{row.sub}</p>}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded" style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>NAICS {row.naics}</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: SKY_PALE, color: '#1E5F8F' }}>{row.evidence}</span>
        <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ backgroundColor: row.stage.bg, color: row.stage.color, letterSpacing: '0.04em' }}>{row.stage.label}</span>
        <span className="text-[10px] ml-auto" style={{ color: TEXT_LIGHT }}>{row.time}</span>
      </div>
    </div>
  );
};

const OperatingSystemPreview = () => {
  const pillars = [
    {
      icon: 'travel_explore',
      label: 'Finder',
      title: 'Discover and qualify',
      detail: 'Federal, state, local, forecast, and teaming signals',
    },
    {
      icon: 'workspaces',
      label: 'Workspace',
      title: 'Run the pursuit',
      detail: 'Requirements, pricing, teaming, proposals, and delivery',
    },
    {
      icon: 'policy',
      label: 'Autonomy',
      title: 'Act within policy',
      detail: 'Atlas monitors, prepares, routes, and escalates governed work',
    },
  ];

  return (
    <div className="w-full" data-testid="operating-system-preview">
      <div className="overflow-hidden rounded-[24px] border border-white/15 bg-white shadow-2xl shadow-black/20">
        <div className="flex items-center justify-between border-b border-slate-200 bg-[#F8FAFC] px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
            <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          </div>
          <span className="rounded-md border border-slate-200 bg-white px-3 py-1 font-mono text-[10px] text-slate-500">app.bidsignaliq.com</span>
          <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Operating system</span>
        </div>

        <div className="bg-[#F4F6FA] p-4 md:p-5">
          <div className="grid gap-2.5 sm:grid-cols-3" aria-label="BidSignalIQ operating model">
            {pillars.map((pillar, index) => (
              <div key={pillar.label} className={`rounded-2xl border p-3.5 ${index === 1 ? 'border-[#5BA9E6] bg-[#EAF5FC]' : 'border-slate-200 bg-white'}`}>
                <div className="flex items-center gap-2">
                  <span className={`material-symbols-outlined text-[19px] ${index === 1 ? 'text-[#1E5F8F]' : 'text-[#C49A00]'}`} aria-hidden="true">{pillar.icon}</span>
                  <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">{pillar.label}</span>
                </div>
                <p className="mt-2 text-sm font-black text-[#0D1F3C]">{pillar.title}</p>
                <p className="mt-1 text-[10px] leading-4 text-slate-500">{pillar.detail}</p>
              </div>
            ))}
          </div>

          <div className="mt-3 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-4 py-3.5">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#0D1F3C] px-2 py-1 text-[9px] font-black uppercase tracking-wide text-white">Mission workspace</span>
                  <span className="rounded-full bg-[#FEF3C7] px-2 py-1 text-[9px] font-black uppercase tracking-wide text-[#8A6500]">Decision review</span>
                </div>
                <p className="mt-2 text-sm font-black text-[#0D1F3C]">VA Healthcare IT Modernization Support</p>
                <p className="mt-0.5 text-[10px] text-slate-500">One pursuit · one accountable record</p>
              </div>
              <button type="button" className="rounded-full bg-[#0D1F3C] px-3 py-2 text-[10px] font-black text-white">Open workspace</button>
            </div>

            <div className="flex gap-1 overflow-hidden border-b border-slate-200 px-3 pt-2" aria-label="Workspace areas">
              {['Overview', 'Requirements', 'Pricing', 'Teaming', 'Proposal', 'Delivery'].map((tab, index) => (
                <span key={tab} className={`whitespace-nowrap border-b-2 px-2 py-2 text-[9px] font-bold ${index === 0 ? 'border-[#C49A00] text-[#0D1F3C]' : 'border-transparent text-slate-400'}`}>{tab}</span>
              ))}
            </div>

            <div className="grid gap-3 p-4 sm:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-xl border border-slate-200 p-3.5">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-500">Decision brief</p>
                  <span className="text-[9px] font-bold text-[#1E5F8F]">Source-linked</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  {[
                    ['Eligibility', 'Review'],
                    ['Evidence', '6 known'],
                    ['Unknowns', '2 questions'],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-lg bg-[#F8FAFC] p-2">
                      <p className="text-[8px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
                      <p className="mt-1 text-[10px] font-black text-[#0D1F3C]">{value}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-[10px] leading-4 text-slate-600"><b className="text-[#0D1F3C]">Next action:</b> Resolve the facility-clearance condition before the governed bid decision.</p>
              </div>

              <div className="rounded-xl border border-[#B8DDF4] bg-[#EAF5FC] p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] font-black uppercase tracking-[0.1em] text-[#1E5F8F]">Atlas autonomy</p>
                  <span className="rounded-full bg-white px-2 py-1 text-[8px] font-black uppercase text-[#0D1F3C]">Approval mode</span>
                </div>
                <ul className="mt-3 space-y-2 text-[10px] text-slate-600">
                  <li className="flex gap-2"><span className="text-emerald-600">●</span><span>Amendment monitoring active</span></li>
                  <li className="flex gap-2"><span className="text-[#C49A00]">●</span><span>Clarification draft ready</span></li>
                  <li className="flex gap-2"><span className="text-[#5BA9E6]">●</span><span>Teaming gap routed for review</span></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <p className="mt-4 text-center text-xs text-slate-400">
        Illustrative product model · organization-specific evidence appears after sign in
      </p>
    </div>
  );
};

/* ============================================================
   Interactive step section
   ============================================================ */
/* ============================================================
   FeatureCardRow — 3 horizontal cards inserted between a section
   headline/subtext and the numbered steps. Used inside OI/DI
   (light theme) and EI (dark navy theme).
   ============================================================ */
const FeatureCardRow = ({ cards, theme = 'light', testid }) => {
  const isDark = theme === 'dark';
  const cardStyle = isDark
    ? {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
      }
    : {
        backgroundColor: '#FFFFFF',
        border: '1px solid #E5E7EB',
        boxShadow: '0 4px 12px rgba(13, 17, 23, 0.04)',
      };
  const titleColor = isDark ? '#FFFFFF' : NAVY;
  const descColor = isDark ? 'rgba(255,255,255,0.72)' : TEXT_MUTED;
  const iconTint = isDark ? 'rgba(196,154,0,0.16)' : 'rgba(196,154,0,0.10)';
  const iconBorder = isDark ? 'rgba(196,154,0,0.30)' : 'rgba(196,154,0,0.22)';

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10"
      data-testid={testid}
    >
      {cards.map((c, i) => {
        const Icon = c.icon;
        return (
          <div
            key={i}
            className="bsiq-capability-card rounded-2xl transition-all"
            style={{ ...cardStyle, padding: '24px' }}
            data-testid={`${testid}-card-${i}`}
          >
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center mb-5"
              style={{ backgroundColor: iconTint, border: `1px solid ${iconBorder}` }}
            >
              <Icon className="w-5 h-5" style={{ color: GOLD }} />
            </div>
            <h3
              className="text-lg font-semibold mb-2"
              style={{ color: titleColor, letterSpacing: '-0.01em' }}
            >
              {c.title}
            </h3>
            <p className="text-sm leading-relaxed" style={{ color: descColor }}>
              {c.description}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const StepSection = ({ id, eyebrowDot, eyebrow, h2, description, steps, demos, reversed = false, note, sectionBg, testid, featureCards }) => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % steps.length), 4000);
    return () => clearInterval(t);
  }, [paused, steps.length]);

  const StepsPanel = (
    <div className="bsiq-step-panel space-y-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      {steps.map((s, i) => {
        const isActive = i === active;
        return (
          <button
            key={i}
            onClick={() => { setActive(i); setPaused(true); }}
            className="w-full min-h-[64px] rounded-xl text-left flex gap-4 items-start transition-all p-4 relative"
            style={{ backgroundColor: isActive ? SKY_PALE : 'transparent', boxShadow: isActive ? `inset 3px 0 0 ${SKY}` : 'none' }}
            data-testid={`${testid}-step-${i}`}
          >
            <div
              className="flex items-center justify-center rounded-full flex-shrink-0 text-sm font-semibold transition-all"
              style={{
                width: '36px', height: '36px',
                backgroundColor: isActive ? NAVY : '#F8FAFC',
                border: isActive ? `2px solid ${NAVY}` : `1.5px solid #CBD5E1`,
                color: isActive ? '#FFFFFF' : '#94A3B8',
              }}
            >{i + 1}</div>
            <div className="min-w-0 flex-1">
              <p className="text-base transition-all" style={{ fontWeight: isActive ? 700 : 500, color: isActive ? NAVY : '#4B5563' }}>{s.label}</p>
              {isActive && (
                <p className="text-sm mt-1.5 bsiq-fade" style={{ color: TEXT_MUTED, lineHeight: 1.6 }}>{s.desc}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const DemoPanel = (
    <div
      className="overflow-hidden bsiq-fade flex flex-col"
      key={active}
      style={{
        backgroundColor: '#FFFFFF',
        border: '1px solid #DCE4ED',
        borderRadius: '20px',
        boxShadow: '0 24px 60px -28px rgba(13,31,60,0.35)',
        minHeight: '520px',
        width: '100%',
      }}
      data-testid={`${testid}-demo`}
    >
      {/* Browser chrome — 44px */}
      <div
        className="flex items-center gap-1.5 px-4 border-b"
        style={{ height: '44px', borderColor: '#E5E7EB', backgroundColor: '#F9FAFB', flexShrink: 0 }}
      >
        <div className="w-2.5 h-2.5 rounded-full bg-red-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-green-400"></div>
        <div className="flex-1 text-center">
          <span className="text-[11px] font-mono px-2 py-0.5 rounded bg-white" style={{ color: TEXT_LIGHT, border: `1px solid ${BORDER}` }}>app.bidsignaliq.com</span>
        </div>
      </div>
      <div style={{ flex: 1, backgroundColor: '#FFFFFF' }}>
        {demos[active]}
      </div>
    </div>
  );

  return (
    <section
      id={id}
      className="px-6 bsiq-marketing-section"
      style={{ backgroundColor: sectionBg, paddingTop: '88px', paddingBottom: '88px' }}
      data-testid={testid}
    >
      <div className="max-w-[1320px] mx-auto">
        <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-6 lg:gap-12 items-end mb-10">
          <div className="bsiq-eyebrow mb-4" style={{ color: NAVY }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: eyebrowDot }}></span>
            {eyebrow}
          </div>
          <div>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.35rem)', color: NAVY }}>{h2}</h2>
            <p className="mt-5 max-w-2xl" style={{ color: TEXT_MUTED, fontSize: '15px' }}>{description}</p>
          </div>
        </div>

        {featureCards && featureCards.length > 0 && (
          <FeatureCardRow cards={featureCards} theme="light" testid={`${testid}-features`} />
        )}

        <div
          className={`grid gap-8 lg:gap-10 items-start ${reversed ? 'lg:grid-cols-[62%_38%]' : 'lg:grid-cols-[38%_62%]'}`}
        >
          <div className={reversed ? 'lg:col-start-2' : ''}>{StepsPanel}</div>
          <div className={reversed ? 'lg:col-start-1 lg:row-start-1' : ''}>{DemoPanel}</div>
        </div>

        {note && (
          <div className="mt-8 max-w-4xl rounded-xl border border-[#B8DDF4] bg-[#EAF5FC] px-5 py-4 text-sm" style={{ color: '#1E5F8F' }}>{note}</div>
        )}
      </div>
    </section>
  );
};

/* ============================================================
   Demo panel builders (small, focused JSX)
   ============================================================ */
const OI_DEMOS = [
  // 1 — portal scanner
  (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-2 h-2 rounded-full bsiq-pulse" style={{ backgroundColor: '#16A34A' }}></span>
        <span className="text-xs font-semibold" style={{ color: NAVY, letterSpacing: '0.08em' }}>BSIQ PORTAL SCANNER</span>
      </div>
      <p className="text-3xl font-bold" style={{ color: NAVY }}>Source-aware <span className="text-lg font-medium" style={{ color: TEXT_MUTED }}>intelligence</span></p>
      <p className="text-xs mt-1" style={{ color: TEXT_LIGHT }}>Representative source pipeline</p>
      <div className="mt-6 space-y-1.5 font-mono text-[11px]">
        {['SAM.gov opportunities', 'USAspending awards', 'Agency forecasts', 'State and local sources', 'Organization watchlists'].map((p, i) => (
          <div key={p} className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: i < 3 ? '#16A34A' : '#CBD5E1' }}></span>
            <span style={{ color: NAVY }}>{p}</span>
            <span className="ml-auto" style={{ color: TEXT_LIGHT }}>{i < 3 ? 'connected' : 'configured'}</span>
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-lg p-4" style={{ backgroundColor: CREAM }}>
        <p className="text-xs uppercase font-medium" style={{ color: TEXT_LIGHT, letterSpacing: '0.08em' }}>Every result retains</p>
        <p className="text-lg font-bold mt-1" style={{ color: GOLD }}>Source · evidence · next action</p>
      </div>
    </div>
  ),
  // 2 — solicitation parsing
  (
    <div className="p-6 text-xs" style={{ color: NAVY, lineHeight: 1.6 }}>
      <p className="font-semibold text-sm mb-3">SOLICITATION: VA-26-0148</p>
      <div className="rounded-lg p-4" style={{ backgroundColor: '#FAFAFA', border: `1px solid ${BORDER}` }}>
        <p><span style={{ backgroundColor: '#FEF3C7', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 }}>Agency: Department of Veterans Affairs</span></p>
        <p className="mt-2">The Contracting Officer requests proposals for <span style={{ backgroundColor: '#FEF3C7', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 }}>NAICS 561320 — Temporary Help Services</span> to support VISN 8 Florida healthcare operations.</p>
        <p className="mt-2">This procurement is designated as a <span style={{ backgroundColor: '#FEF3C7', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 }}>Set-Aside: WOSB (Women-Owned Small Business)</span>.</p>
        <p className="mt-2">Response deadline: <span style={{ backgroundColor: '#FEF3C7', padding: '1px 4px', borderRadius: '3px', fontWeight: 600 }}>August 10, 2026, 5:00 PM EDT</span>.</p>
        <p className="mt-2">Estimated performance period: 12 months base + 4 option years.</p>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
        {[
          { l: 'NAICS', v: '561320' },
          { l: 'Set-Aside', v: 'WOSB' },
          { l: 'Deadline', v: 'Aug 10, 2026' },
          { l: 'Agency', v: 'VA VISN 8' },
        ].map((f) => (
          <div key={f.l} className="rounded p-2" style={{ backgroundColor: CREAM }}>
            <p className="text-[10px] uppercase" style={{ color: TEXT_LIGHT, letterSpacing: '0.08em' }}>{f.l}</p>
            <p className="font-semibold" style={{ color: NAVY }}>{f.v}</p>
          </div>
        ))}
      </div>
    </div>
  ),
  // 3 — eligibility check
  (
    <div className="p-6">
      <p className="text-sm font-semibold mb-4" style={{ color: NAVY }}>Eligibility Check · VA-26-0148</p>
      <div className="space-y-2 text-sm">
        {[
          { ok: true, label: 'WOSB Certification', v: 'Verified · exp 2027-04' },
          { ok: true, label: 'NAICS 561320', v: 'Primary code on SAM profile' },
          { ok: true, label: 'Size Standard', v: 'Revenue < $30M ✓' },
          { ok: true, label: 'Geography', v: 'FL Region — Active' },
          { ok: false, label: 'Secret Clearance Required', v: 'Not held — DISQUALIFIED' },
        ].map((r, i) => (
          <div key={i} className="flex items-center gap-3 rounded-lg p-3" style={{ backgroundColor: r.ok ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${r.ok ? '#BBF7D0' : '#FECACA'}` }}>
            <span className="material-symbols-outlined text-lg" style={{ color: r.ok ? '#16A34A' : '#DC2626' }}>{r.ok ? 'check_circle' : 'cancel'}</span>
            <div className="flex-1">
              <p className="text-xs font-semibold" style={{ color: NAVY }}>{r.label}</p>
              <p className="text-[11px]" style={{ color: TEXT_MUTED }}>{r.v}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  ),
  // 4 — score dial
  (
    <div className="p-6 grid grid-cols-2 gap-6 items-center">
      <div className="text-center">
        <div className="relative inline-block">
          <svg width="160" height="160" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="66" fill="none" stroke="#F3F4F6" strokeWidth="14" />
            <circle cx="80" cy="80" r="66" fill="none" stroke={GOLD} strokeWidth="14" strokeDasharray={`${(88 / 100) * 414} 414`} strokeLinecap="round" transform="rotate(-90 80 80)" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-4xl font-bold" style={{ color: NAVY }}>88</p>
            <p className="text-xs" style={{ color: TEXT_LIGHT }}>/ 100</p>
          </div>
        </div>
        <p className="text-sm font-semibold mt-3" style={{ color: NAVY }}>Bid Fit Score</p>
      </div>
      <div className="space-y-3 text-xs">
        {[
          { l: 'NAICS Match', v: 95 },
          { l: 'Set-Aside Match', v: 100 },
          { l: 'Past Performance', v: 82 },
          { l: 'Geographic', v: 80 },
        ].map((r) => (
          <div key={r.l}>
            <div className="flex justify-between mb-1">
              <span style={{ color: TEXT_MUTED }}>{r.l}</span>
              <span className="font-semibold" style={{ color: NAVY }}>{r.v}%</span>
            </div>
            <div className="h-1.5 rounded-full" style={{ backgroundColor: '#F3F4F6' }}>
              <div className="h-full rounded-full" style={{ width: r.v + '%', backgroundColor: GOLD }}></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  ),
  // 5 — disqualifier flags
  (
    <div className="p-6">
      <p className="text-sm font-semibold mb-4" style={{ color: NAVY }}>Risk & Disqualifier Flags</p>
      <div className="space-y-2 text-sm">
        {[
          { icon: 'schedule', tone: '#B45309', label: 'Timeline: 14 days to respond', desc: 'Tight — start capture immediately' },
          { icon: 'warning', tone: '#DC2626', label: 'Incumbent advantage: high', desc: 'Observed award history requires review' },
          { icon: 'verified', tone: '#15803D', label: 'Set-aside: WOSB — eligible', desc: 'Certification verified' },
          { icon: 'help', tone: '#B45309', label: 'Prior agency work unknown', desc: 'Past-performance evidence requires review' },
        ].map((r, i) => (
          <div key={i} className="flex items-start gap-3 rounded-lg p-3" style={{ backgroundColor: '#FAFAFA', border: `1px solid ${BORDER}` }}>
            <span className="material-symbols-outlined text-lg" aria-hidden="true" style={{ color: r.tone }}>{r.icon}</span>
            <div>
              <p className="text-xs font-semibold" style={{ color: NAVY }}>{r.label}</p>
              <p className="text-[11px] mt-0.5" style={{ color: TEXT_MUTED }}>{r.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  ),
];

const DI_DEMOS = [
  // 1 — missions list
  (
    <div className="p-4">
      <p className="text-sm font-semibold mb-3 px-2" style={{ color: NAVY }}>Missions List · 5 qualified</p>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ color: TEXT_LIGHT }}>
            <th className="text-left font-medium py-2 px-2">Agency</th>
            <th className="text-left font-medium py-2 px-2">Title</th>
            <th className="text-right font-medium py-2 px-2">Score</th>
            <th className="text-left font-medium py-2 px-2">Stage</th>
            <th className="text-left font-medium py-2 px-2">Deadline</th>
          </tr>
        </thead>
        <tbody>
          {[
            { a: 'VA VISN 8', t: 'Healthcare Staffing Support', s: 91, st: 'DECIDE', d: 'DUE AUG 10', hi: true },
            { a: 'HHS IHS', t: 'RN Staffing — Phoenix', s: 88, st: 'QUALIFY', d: 'Aug 24' },
            { a: 'DHA', t: 'Allied Health Sources Sought', s: 79, st: 'DISCOVER', d: 'Sep 12' },
            { a: 'DoD', t: 'Cyber Support Services', s: 74, st: 'DISCOVER', d: 'Sep 30' },
            { a: 'GSA', t: 'IT Modernization Advisory', s: 68, st: 'DISCOVER', d: 'Oct 15' },
          ].map((r, i) => (
            <tr key={i} style={{ backgroundColor: r.hi ? GOLD_BG : 'transparent', borderTop: `1px solid ${BORDER}` }}>
              <td className="py-2.5 px-2 font-semibold" style={{ color: NAVY }}>{r.a}</td>
              <td className="py-2.5 px-2" style={{ color: NAVY }}>{r.t}</td>
              <td className="py-2.5 px-2 text-right font-bold" style={{ color: r.s >= 85 ? '#15803D' : r.s >= 70 ? '#B45309' : TEXT_MUTED }}>{r.s}%</td>
              <td className="py-2.5 px-2 text-[10px] font-semibold" style={{ color: NAVY }}>{r.st}</td>
              <td className="py-2.5 px-2 text-[11px]" style={{ color: r.hi ? '#B45309' : TEXT_MUTED, fontWeight: r.hi ? 700 : 400 }}>{r.d}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ),
  // 2 — Atlas chat
  (
    <div className="p-5 space-y-3">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: GOLD }}>
          <span className="material-symbols-outlined text-white text-sm">smart_toy</span>
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: NAVY }}>Atlas</p>
          <p className="text-[10px]" style={{ color: '#16A34A' }}>● Online</p>
        </div>
      </div>
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl rounded-tr-sm px-3 py-2" style={{ backgroundColor: NAVY }}>
          <p className="text-xs text-white">Who's the incumbent on the VA VISN 8 staffing contract?</p>
        </div>
      </div>
      <div className="flex gap-2">
        <div className="w-6 h-6 rounded flex items-center justify-center mt-1 flex-shrink-0" style={{ backgroundColor: GOLD }}>
          <span className="material-symbols-outlined text-white text-xs">smart_toy</span>
        </div>
        <div className="max-w-[80%] rounded-2xl rounded-tl-sm px-3 py-2.5" style={{ backgroundColor: '#F3F4F6' }}>
          <p className="text-xs leading-relaxed" style={{ color: NAVY }}>
            The current incumbent is <b>MedTravelers (AMN Healthcare)</b> — awarded <b>$4.2M in 2023</b> under contract VA-23-0089. Award history shows 3 consecutive renewals. Strong incumbent advantage — recommend competitive pricing strategy.
          </p>
        </div>
      </div>
    </div>
  ),
  // 3 — CO Intelligence
  (
    <div className="p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold" style={{ backgroundColor: NAVY, color: GOLD }}>JW</div>
        <div>
          <p className="text-base font-semibold" style={{ color: NAVY }}>Jennifer Walsh</p>
          <p className="text-xs" style={{ color: TEXT_LIGHT }}>Contracting Officer · VA VISN 8</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[{ l: 'Avg Contract', v: '$2.8M' }, { l: 'Preferred Set-Aside', v: 'WOSB' }, { l: 'Active Awards', v: '12' }].map((c) => (
          <div key={c.l} className="rounded p-2 text-center" style={{ backgroundColor: CREAM }}>
            <p className="text-lg font-bold" style={{ color: NAVY }}>{c.v}</p>
            <p className="text-[9px] uppercase" style={{ color: TEXT_LIGHT, letterSpacing: '0.06em' }}>{c.l}</p>
          </div>
        ))}
      </div>
      <p className="text-xs font-semibold mb-2" style={{ color: NAVY }}>Recent awards</p>
      <div className="space-y-1.5 text-[11px]">
        {[
          ['2025-Q3', 'MedTravelers · $4.2M', 'Nurse Staffing'],
          ['2025-Q1', 'ClearLine Health · $1.9M', 'Allied Health'],
          ['2024-Q4', 'PulseHR · $890K', 'Temporary Staffing'],
        ].map((r, i) => (
          <div key={i} className="flex gap-2 py-1.5 border-b" style={{ borderColor: BORDER, color: NAVY }}>
            <span style={{ color: TEXT_LIGHT, minWidth: '52px' }}>{r[0]}</span>
            <span className="font-semibold">{r[1]}</span>
            <span className="ml-auto" style={{ color: TEXT_LIGHT }}>{r[2]}</span>
          </div>
        ))}
      </div>
    </div>
  ),
  // 4 — Pricing War Room
  (
    <div className="p-6">
      <p className="text-sm font-semibold mb-4" style={{ color: NAVY }}>Pricing War Room · NAICS 561320 (RN)</p>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[
          { src: 'BLS Wage', v: '$42.50/hr', d: 'Median RN — FL' },
          { src: 'GSA CALC', v: '$89.00/hr', d: 'Loaded rate — schedule' },
          { src: 'USASpending', v: '$91.00/hr', d: 'Incumbent billed rate' },
        ].map((c) => (
          <div key={c.src} className="rounded-lg p-3" style={{ backgroundColor: '#FAFAFA', border: `1px solid ${BORDER}` }}>
            <p className="text-[10px] uppercase font-medium" style={{ color: TEXT_LIGHT, letterSpacing: '0.06em' }}>{c.src}</p>
            <p className="text-lg font-bold mt-1" style={{ color: NAVY }}>{c.v}</p>
            <p className="text-[10px] mt-0.5" style={{ color: TEXT_MUTED }}>{c.d}</p>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-4" style={{ backgroundColor: GOLD_BG, border: `1px solid ${GOLD}` }}>
        <p className="text-[11px] uppercase font-semibold" style={{ color: '#B45309', letterSpacing: '0.06em' }}>Recommended loaded rate</p>
        <p className="text-3xl font-bold mt-1" style={{ color: NAVY }}>$87.50<span className="text-sm font-medium">/hr</span></p>
        <p className="text-[11px] mt-1" style={{ color: TEXT_MUTED }}>Under incumbent, market-competitive · margin healthy</p>
      </div>
    </div>
  ),
  // 5 — pipeline tracker
  (
    <div className="p-6">
      <p className="text-sm font-semibold mb-6" style={{ color: NAVY }}>Bid Pipeline · Live</p>
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { l: 'DISCOVER', v: 12, c: '#94A3B8' },
          { l: 'QUALIFY',  v: 8,  c: '#2563EB' },
          { l: 'DECIDE',   v: 3,  c: GOLD },
          { l: 'BID',      v: 1,  c: '#16A34A' },
        ].map((s, i) => (
          <div key={s.l} className="text-center">
            <div className="rounded-lg py-4" style={{ backgroundColor: '#FAFAFA', border: `1px solid ${BORDER}` }}>
              <p className="text-3xl font-bold" style={{ color: s.c }}>{s.v}</p>
              <p className="text-[10px] uppercase font-semibold mt-1" style={{ color: TEXT_MUTED, letterSpacing: '0.06em' }}>{s.l}</p>
            </div>
            {i < 3 && <div className="hidden">→</div>}
          </div>
        ))}
      </div>
      <div className="rounded-lg p-4 flex items-center gap-4" style={{ backgroundColor: CREAM }}>
        <div>
          <p className="text-[10px] uppercase font-semibold" style={{ color: TEXT_LIGHT, letterSpacing: '0.06em' }}>RFI → RFP Conversion</p>
          <p className="text-2xl font-bold mt-0.5" style={{ color: NAVY }}>28%</p>
        </div>
        <div className="border-l pl-4" style={{ borderColor: BORDER }}>
          <p className="text-[10px] uppercase font-semibold" style={{ color: TEXT_LIGHT, letterSpacing: '0.06em' }}>Avg Lead Time</p>
          <p className="text-2xl font-bold mt-0.5" style={{ color: NAVY }}>9 mo</p>
        </div>
      </div>
    </div>
  ),
];

/* ============================================================
   HomePage
   ============================================================ */
const HomePage = () => {
  const navigate = useNavigate();

  // EI section tabs (horizontal at top)
  const [eiTab, setEiTab] = useState(0);
  const eiTabs = [
    { label: 'AI Proposal Writer' },
    { label: 'Amendment Alerts' },
    { label: 'Human Governance Layer' },
  ];
  const eiDemos = [
    (
      <div className="p-6" style={{ color: '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold">Technical Approach — Draft 1</p>
          <span className="text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'rgba(196,154,0,0.2)', color: GOLD }}>SECTION L RESPONSE</span>
        </div>
        <div className="rounded-lg p-4 text-xs" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', lineHeight: 1.7 }}>
          <p>Our staffing approach for VISN 8 will deploy a dedicated <span style={{ color: GOLD, fontWeight: 600 }}>WOSB-certified</span> team across the identified facilities. Section 4.2 requires continuous coverage; the draft links each staffing assumption to the approved company evidence available for review.<span className="inline-block ml-1" style={{ backgroundColor: '#FFFFFF', width: '2px', height: '14px', verticalAlign: 'middle', animation: 'bsiq-pulse-dot 1s ease-in-out infinite' }}></span></p>
        </div>
        <p className="text-[11px] font-semibold mt-4 mb-2" style={{ letterSpacing: '0.06em', color: '#94A3B8' }}>SECTION M COMPLIANCE</p>
        <div className="space-y-1.5">
          {['Technical Approach — Complete', 'Management Plan — Complete', 'Past Performance — In progress'].map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="material-symbols-outlined text-sm" style={{ color: i < 2 ? '#4ADE80' : '#94A3B8', fontVariationSettings: "'FILL' 1" }}>{i < 2 ? 'check_circle' : 'schedule'}</span>
              {s}
            </div>
          ))}
        </div>
      </div>
    ),
    (
      <div className="p-6" style={{ color: '#FFFFFF' }}>
        <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'rgba(196,154,0,0.14)', border: `1px solid ${GOLD}` }}>
          <div className="flex items-center gap-2 mb-2">
            <span className="material-symbols-outlined" aria-hidden="true" style={{ color: GOLD }}>bolt</span>
            <span className="text-xs font-bold" style={{ color: GOLD, letterSpacing: '0.08em' }}>AMENDMENT DETECTED — 15 MIN AGO</span>
          </div>
          <p className="text-sm font-semibold mb-2">VA-26-0148 · Amendment 0002</p>
          <p className="text-xs leading-relaxed" style={{ color: '#CBD5E1' }}>
            Page 12, Section 4.2 — <span style={{ color: '#FFFFFF', fontWeight: 600 }}>Performance period extended from 12 to 18 months.</span> Response deadline unchanged: Aug 10.
          </p>
        </div>
        <div className="rounded-lg p-3 flex items-center gap-3" style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}>
          <span className="material-symbols-outlined" style={{ color: GOLD }}>schedule</span>
          <div className="text-xs">
            <p style={{ color: '#FFFFFF', fontWeight: 600 }}>Material change ready for review</p>
            <p style={{ color: '#94A3B8' }}>Affected requirements and deadlines remain attached to the Mission.</p>
          </div>
        </div>
      </div>
    ),
    (
      <div className="p-6" style={{ color: '#FFFFFF' }}>
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm font-semibold">Review Queue · 7 items pending</p>
          <span className="text-[10px] px-2 py-1 rounded" style={{ backgroundColor: 'rgba(74,222,128,0.15)', color: '#4ADE80' }}>HUMAN GOVERNANCE</span>
        </div>
        <div className="space-y-2 text-xs">
          {[
            'Executive Summary — draft ready',
            'Section L.4.2 technical approach',
            'Pricing narrative — GSA CALC output',
          ].map((s, i) => (
            <div key={i} className="rounded-lg p-3 flex items-center justify-between" style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.10)' }}>
              <div className="flex-1">
                <p className="text-[10px] uppercase" style={{ color: '#94A3B8', letterSpacing: '0.06em' }}>Atlas Suggestion</p>
                <p className="mt-0.5">{s}</p>
              </div>
              <div className="flex gap-1.5 ml-3">
                <button className="text-[10px] font-bold px-2.5 py-1 rounded" style={{ backgroundColor: '#4ADE80', color: NAVY }}>Approve</button>
                <button className="text-[10px] font-bold px-2.5 py-1 rounded" style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    ),
  ];

  return (
    <div className="bsiq-home min-h-screen" style={{ backgroundColor: CREAM }} data-testid="homepage">
      <LandingNav />

      <main className="pt-16">
        {/* ============ HERO ============ */}
        <section className="relative overflow-hidden bsiq-marketing-hero" data-testid="hero-section">
          <div className="absolute inset-0 bsiq-marketing-hero__glow pointer-events-none" aria-hidden="true" />
          <div className="max-w-[1320px] mx-auto px-6 pt-16 lg:pt-20 pb-16 lg:pb-20 relative">
            <div className="grid lg:grid-cols-[42%_58%] gap-12 lg:gap-16 items-center">
              {/* Left */}
              <div className="relative">
                <div className="absolute inset-0 -m-12 bsiq-line-grid bsiq-line-grid--dark pointer-events-none"></div>
                <div className="relative">
                  <span
                    className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full mb-6"
                    style={{ backgroundColor: 'rgba(91,169,230,0.14)', color: '#BFE2F8', border: '1px solid rgba(91,169,230,0.28)' }}
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: SKY }}></span>
                    Opportunity intelligence to accountable execution
                  </span>
                  <h1 style={{ fontSize: 'clamp(2.75rem, 5vw, 4.25rem)', color: '#FFFFFF' }}>
                    Run government contracting from one operating system.
                  </h1>
                  <p className="mt-6 max-w-xl" style={{ color: '#CBD5E1', fontSize: '16px', lineHeight: 1.75 }}>
                    BidSignalIQ connects opportunity discovery, evidence-based qualification, capture, teaming, pricing, proposals, and delivery around the pursuit—not across disconnected tools.
                  </p>
                  <div className="flex flex-wrap gap-3 mt-8">
                    <BsiqActionButton variant="gold"
                      onClick={openCalendly}
                      data-testid="hero-request-demo-btn"
                    >
                      Request a demo
                    </BsiqActionButton>
                    <BsiqActionButton
                      variant="inverse"
                      onClick={() => document.getElementById('atlas')?.scrollIntoView({ behavior: 'smooth' })}
                      data-testid="hero-atlas-btn"
                    >
                      See the workflow
                    </BsiqActionButton>
                  </div>
                  <p className="mt-5 text-sm" style={{ color: '#94A3B8' }}>
                    Built for governed autonomy, traceable evidence, and human authority.
                  </p>
                </div>
              </div>

              {/* Right — GovCon BD operating system preview */}
              <div className="flex justify-center lg:justify-end">
                <OperatingSystemPreview />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-px mt-14 overflow-hidden rounded-2xl border border-white/10 bg-white/10" aria-label="BidSignalIQ operating principles">
              {[
                ['Source-linked', 'Requirements and decisions retain their evidence.'],
                ['Unknown-aware', 'Missing information becomes a question, not a guess.'],
                ['Human-governed', 'Your policy controls consequential actions.'],
              ].map(([title, body]) => (
                <div key={title} className="bg-[#0B1A31] px-6 py-5">
                  <p className="text-sm font-bold text-white">{title}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ OPERATING LIFECYCLE ============ */}
        <section
          className="border-t border-b"
          style={{ borderColor: BORDER, backgroundColor: '#FFFFFF' }}
          data-testid="lifecycle-rail"
          aria-labelledby="lifecycle-title"
        >
          <div className="max-w-[1320px] mx-auto px-6 py-10">
            <p id="lifecycle-title" className="text-xs font-semibold uppercase tracking-[0.12em] mb-6" style={{ color: TEXT_MUTED }}>
              One opportunity-centered operating lifecycle
            </p>
            <ol className="bsiq-lifecycle" aria-label="BidSignalIQ opportunity lifecycle">
              {['Signal', 'Qualify', 'Clarify', 'Decide', 'Team', 'Staff', 'Propose', 'Deliver'].map((stage, index) => (
                <li key={stage} className="bsiq-lifecycle__step">
                  <span className="bsiq-lifecycle__index">{index + 1}</span>
                  <span className="block mt-3 text-xs font-semibold" style={{ color: NAVY }}>{stage}</span>
                </li>
              ))}
            </ol>
            <div className="grid md:grid-cols-3 gap-4 mt-8" aria-label="Opportunity workflow continuity">
              {[
                {
                  title: 'Detailed requirement intelligence',
                  description: 'Turn solicitation language into cited requirements, conditions, deadlines, evidence needs, and unresolved questions—not just a short summary.',
                },
                {
                  title: 'Decision to Mission',
                  description: 'Convert an approved pursuit into one governed Mission with its decisions, owners, requirements, documents, tasks, and history already attached.',
                },
                {
                  title: 'Private teaming by design',
                  description: 'Run separate candidate conversations for each capability gap. Potential partners never see another company, response, rate, or internal evaluation.',
                },
              ].map((item) => (
                <div key={item.title} className="rounded-2xl border bg-white p-5 shadow-sm" style={{ borderColor: BORDER }}>
                  <p className="text-sm font-semibold" style={{ color: NAVY }}>{item.title}</p>
                  <p className="text-xs mt-2 leading-5" style={{ color: TEXT_MUTED }}>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Anchor alias for legacy /features redirect */}
        <div id="features" style={{ position: 'relative', top: '-80px' }} aria-hidden="true" />

        {/* ============ SECTION 4 — OPPORTUNITY INTELLIGENCE ============ */}
        <StepSection
          id="oi"
          testid="oi-section"
          sectionBg={CREAM}
          eyebrowDot={SKY}
          eyebrow="OPPORTUNITY INTELLIGENCE"
          h2="Relevant opportunities, qualified before you pursue them."
          description="Before a proposal is written, BidSignalIQ structures opportunity data, evaluates evidence against your company profile, and exposes disqualifiers and unknowns for review."
          featureCards={[
            {
              icon: Radar,
              title: 'Source Intelligence',
              description: 'Bring procurement signals and source records into one normalized opportunity workflow with traceable provenance.',
            },
            {
              icon: ShieldCheck,
              title: 'Eligibility First',
              description: 'Evaluate opportunities against your NAICS codes, set-asides, size standards, geography, and available company evidence before committing pursuit resources.',
            },
            {
              icon: BellRing,
              title: 'Amendment Intelligence',
              description: 'Track material changes, deadlines, and unresolved requirements without relying on inbox memory.',
            },
          ]}
          steps={[
            { label: 'Collect opportunity signals', desc: 'Normalize procurement notices and source records into an opportunity-centered intelligence workflow.' },
            { label: 'Extract detailed requirements', desc: 'Structure mandatory and conditional requirements, licenses, clearances, locations, labor needs, dates, submission instructions, evaluation factors, and source citations.' },
            { label: 'Run eligibility check',   desc: 'Compare solicitation requirements with company evidence and surface satisfied conditions, disqualifiers, remediable gaps, and questions that still need an answer.' },
            { label: 'Evaluate decision dimensions', desc: 'Review eligibility, capability alignment, staffing feasibility, schedule, evidence completeness, and unresolved conditions separately.' },
            { label: 'Flag disqualifiers',      desc: 'Clearance requirements, geographic restrictions, incumbent lock-in risk, and short timelines are flagged before you invest time in the opportunity.' },
          ]}
          demos={OI_DEMOS}
        />

        {/* ============ SECTION 5 — DECISION INTELLIGENCE (id=atlas, reversed) ============ */}
        <StepSection
          id="atlas"
          testid="di-section"
          sectionBg="#FFFFFF"
          reversed
          eyebrowDot={GOLD}
          eyebrow="DECISION INTELLIGENCE"
          h2="Know whether to bid before you write a single word."
          description="Use Atlas inside the opportunity workflow to interpret requirements, surface evidence, explain risks, and prepare the next governed action."
          featureCards={[
            {
              icon: MessageSquare,
              title: 'Atlas AI',
              description: 'Ask Atlas about a solicitation, incumbent context, pricing evidence, or CO history and keep the answer attached to the pursuit.',
            },
            {
              icon: UserCheck,
              title: 'CO Intelligence',
              description: "Review the Contracting Officer's award history, vendor concentration, and observable acquisition patterns before you engage.",
            },
            {
              icon: LineChart,
              title: 'Pricing War Room',
              description: 'Combine available BLS wage data, GSA CALC labor rates, and USASpending award evidence in reviewable pricing scenarios.',
            },
          ]}
          steps={[
            { label: 'Review your scored pipeline', desc: 'Your Missions List shows every qualified opportunity sorted by match score. Color-coded by stage: DISCOVER, QUALIFY, DECIDE, BID.' },
            { label: 'Ask Atlas AI',                desc: 'Atlas uses your pipeline, company profile, and available solicitation evidence to prepare a sourced answer that stays attached to the pursuit.' },
            { label: 'Check CO Intelligence',        desc: 'Review sourced award history, vendor concentration, and observable acquisition patterns associated with the Contracting Officer.' },
            { label: 'Run Pricing War Room',         desc: 'Use available labor-rate and award evidence to build, compare, and approve pricing scenarios.' },
            { label: 'Decide and open the Mission',  desc: 'Record Bid, Review Later, No Bid, or Monitor with its rationale. An approved pursuit moves into a governed Mission without losing its evidence or history.' },
          ]}
          demos={DI_DEMOS}
        />

        {/* ============ SECTION 6 — EXECUTION INTELLIGENCE (dark) ============ */}
        <section id="ei" className="px-6 bsiq-execution-section" style={{ backgroundColor: NAVY, color: '#FFFFFF', paddingTop: '88px', paddingBottom: '88px' }} data-testid="ei-section">
          <div className="max-w-[1320px] mx-auto">
            <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-6 lg:gap-12 items-end mb-10">
              <div className="bsiq-eyebrow" style={{ color: '#BFE2F8' }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }}></span>
                EXECUTION INTELLIGENCE
              </div>
              <div>
                <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.35rem)', color: '#FFFFFF' }}>From qualified pursuit to a controlled submission workflow.</h2>
                <p className="mt-5 max-w-2xl" style={{ color: '#B8C5D6', fontSize: '15px' }}>
                  Once you decide to bid, BSIQ's proposal workspace, amendment intelligence, and governance controls support a traceable path from requirements to reviewed submission materials.
                </p>
              </div>
            </div>

            {/* Feature card row — above the tab switcher */}
            <FeatureCardRow
              theme="dark"
              testid="ei-section-features"
              cards={[
                {
                  icon: FileText,
                  title: 'AI Proposal Writer',
                  description: 'Prepare source-grounded Section L/M-aligned first drafts from approved past performance and solicitation requirements for human review.',
                },
                {
                  icon: RefreshCw,
                  title: 'Amendment Alerts',
                  description: 'Monitor supported-source amendments on scheduled refreshes and surface material deadline, scope, and Q&A changes for review.',
                },
                {
                  icon: Eye,
                  title: 'Human Governance Layer',
                  description: 'Every AI output requires human review before submission. You stay in control — AI handles the drafting, you handle the decisions.',
                },
              ]}
            />

            {/* Tabs */}
            <div className="inline-flex flex-wrap gap-2 mb-6 rounded-xl border border-white/10 bg-white/5 p-2">
              {eiTabs.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setEiTab(i)}
                  className="text-sm font-medium px-5 py-2.5 rounded-md transition-all"
                  data-testid={`ei-tab-${i}`}
                  style={{
                    backgroundColor: eiTab === i ? '#FFFFFF' : 'transparent',
                    color: eiTab === i ? NAVY : '#FFFFFF',
                    fontWeight: eiTab === i ? 700 : 500,
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <div key={eiTab} className="rounded-2xl overflow-hidden bsiq-fade" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', minHeight: '400px', boxShadow: '0 24px 60px -32px rgba(0,0,0,0.65)' }}>
              {eiDemos[eiTab]}
            </div>

            {/* Stats cards */}
            <div className="grid md:grid-cols-3 gap-4 mt-10">
              {[
                { v: 'Traceable', l: 'Source Evidence', bg: GOLD, fg: NAVY },
                { v: 'Reviewable', l: 'Unknowns and Exceptions', bg: '#94A3B8', fg: NAVY },
                { v: 'Governed', l: 'Approvals and Audit History', bg: '#1E3A5F', fg: '#FFFFFF' },
              ].map((c, i) => (
                <div key={i} className="rounded-xl p-6" style={{ backgroundColor: c.bg, color: c.fg }}>
                  <p className="text-3xl font-bold">{c.v}</p>
                  <p className="text-xs mt-1 opacity-80">{c.l}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SECTION 7 — DOWNLOAD ============ */}
        <section
          id="download"
          className="px-6"
          style={{ backgroundColor: '#F4F6FA', paddingTop: '72px', paddingBottom: '72px' }}
          data-testid="download-section"
        >
          <div className="max-w-[1320px] mx-auto overflow-hidden rounded-[28px] bg-[#0B1A31] shadow-xl">
            <div className="grid lg:grid-cols-[1.35fr_0.65fr]">
              <div className="p-8 md:p-12 lg:p-16">
                <div className="bsiq-eyebrow mb-5" style={{ color: '#BFE2F8' }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SKY }} />ATLAS CLOUD + DESKTOP</div>
                <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: '#FFFFFF' }}>Atlas stays with the pursuit.</h2>
                <p className="mt-5 max-w-2xl" style={{ color: '#B8C5D6', fontSize: '15px', lineHeight: 1.7 }}>
                  With an active BidSignalIQ cloud workspace, Atlas can continue policy-approved monitoring and workflow activity when your computer is off. Atlas Desktop provides access to the same opportunity context when you are working locally.
                </p>
                <p className="text-xs mt-6" style={{ color: '#94A3B8' }}>Included with your BidsignalIQ workspace · macOS 12+ · Windows 10+</p>
              </div>
              <div className="flex flex-col justify-center gap-4 border-t border-white/10 bg-white/5 p-8 lg:border-l lg:border-t-0 lg:p-12">
                <p className="text-sm font-semibold text-white">Continue in your workspace</p>
                <p className="text-xs leading-5 text-slate-400">Start a trial or sign in to access the secure desktop installer.</p>
                <div className="mt-3 flex flex-col gap-3">
                  <BsiqActionButton variant="gold" onClick={() => openApp('/register')} data-testid="download-section-start-trial">Start 7-day trial</BsiqActionButton>
                  <BsiqActionButton variant="inverse" onClick={() => openApp('/login')} data-testid="download-section-sign-in">Sign In to Download</BsiqActionButton>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ SECTION 7 — PRICING ============ */}
        <section id="pricing" className="px-6" style={{ backgroundColor: '#FFFFFF', paddingTop: '88px', paddingBottom: '88px' }} data-testid="pricing-section">
          <div className="max-w-[1120px] mx-auto grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-16 items-start">
            <div>
            <div className="bsiq-eyebrow mb-6" style={{ color: NAVY }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }}></span>
              PRICING
            </div>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)', color: NAVY }}>Intelligence that pays for itself. Pricing built around how you win.</h2>
            <p className="mt-6" style={{ color: TEXT_MUTED, fontSize: '14px' }}>
              BidSignalIQ pricing is based on deployed capabilities and workflow scope—not seat count. We tailor the operating model to the work your team needs to control.
            </p>
            <div className="flex flex-wrap gap-3 mt-9">
              <BsiqActionButton onClick={openCalendly} data-testid="pricing-request-btn">Request pricing</BsiqActionButton>
              <BsiqActionButton variant="secondary" onClick={openCalendly} data-testid="pricing-demo-btn">See a live demo</BsiqActionButton>
            </div>
            </div>
            <div className="rounded-3xl border border-slate-200 bg-[#F8FAFC] p-7 md:p-9 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-[0.12em]" style={{ color: '#1E5F8F' }}>Included operating capabilities</p>
            <ul className="mt-7 space-y-4">
              {[
                'Surface relevant opportunities with source evidence and next actions',
                'Qualify bids in minutes, not days of manual SAM.gov research',
                'Monitor supported-source amendments and response deadlines',
                'Build reviewable pricing scenarios from available market evidence',
                'Prepare source-grounded Section L/M-aligned proposal drafts',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 border-b border-slate-200 pb-4 text-sm last:border-0 last:pb-0" style={{ color: NAVY }}>
                  <span className="material-symbols-outlined mt-0.5 text-lg" aria-hidden="true" style={{ color: '#1E5F8F' }}>check_circle</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
              <p className="text-xs mt-7 rounded-xl border border-[#B8DDF4] bg-[#EAF5FC] p-4" style={{ color: '#1E5F8F' }}>Commercial scope and deployment terms are confirmed before activation.</p>
            </div>
          </div>
        </section>

        {/* ============ SECTION 8 — TRUST & GOVERNANCE ============ */}
        <section id="compare" className="px-6 py-20" style={{ backgroundColor: CREAM }} data-testid="trust-governance-section">
          <div className="max-w-[1320px] mx-auto">
            <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-6 lg:gap-12 items-end mb-10">
              <div className="bsiq-eyebrow mb-4" style={{ color: NAVY }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: GOLD }}></span>
                TRUST &amp; GOVERNANCE
              </div>
              <div><h2 style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', color: NAVY }}>
                Intelligence your team can inspect, challenge, and approve.
              </h2>
              <p className="mt-5" style={{ color: TEXT_MUTED, fontSize: '15px' }}>
                BidSignalIQ keeps consequential decisions accountable with visible evidence, explicit unknowns, controlled actions, and an opportunity-level history.
              </p></div>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: 'fact_check', title: 'Evidence before conclusions', body: 'Qualification findings retain their source, rule, result, and unresolved questions.' },
                { icon: 'approval', title: 'Human approval where it matters', body: 'Bid decisions, pricing, commitments, certifications, and submissions remain accountable.' },
                { icon: 'history', title: 'Opportunity-level memory', body: 'Actions, documents, amendments, decisions, and collaboration stay attached to the pursuit.' },
              ].map((item) => (
                <article key={item.title} className="bsiq-trust-card rounded-2xl border p-7" style={{ borderColor: BORDER, backgroundColor: '#FFFFFF' }}>
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#EAF5FC] material-symbols-outlined" aria-hidden="true" style={{ color: '#1E5F8F', fontSize: '24px' }}>{item.icon}</span>
                  <h3 className="mt-5 text-lg" style={{ color: NAVY }}>{item.title}</h3>
                  <p className="mt-3" style={{ color: TEXT_MUTED }}>{item.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ============ SECTION 9 — FINAL CTA ============ */}
        <section className="px-6" style={{ backgroundColor: '#F4F6FA', paddingTop: '72px', paddingBottom: '72px' }} data-testid="final-cta-section">
          <div className="max-w-[1120px] mx-auto rounded-[28px] bg-[#0D1F3C] px-7 py-12 text-center shadow-xl md:px-12 md:py-16">
            <div className="bsiq-eyebrow justify-center mb-5" style={{ color: '#BFE2F8' }}><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: SKY }} />SEE BIDSIGNALIQ IN YOUR MARKET</div>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', color: '#FFFFFF' }}>
              Stop losing contracts to contractors who saw the opportunity first.
            </h2>
            <ul className="mt-8 text-left space-y-3 max-w-lg mx-auto">
              {[
                'Surface relevant bids with evidence and next actions',
                'Qualify opportunities in minutes',
                'Monitor supported-source amendments and deadlines',
                'Build pricing scenarios from available market evidence',
                'Prepare source-grounded proposal drafts for human review',
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm" style={{ color: '#CBD5E1' }}>
                  <span className="material-symbols-outlined text-lg" aria-hidden="true" style={{ color: '#7CC4EF' }}>check_circle</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap justify-center gap-3 mt-10">
              <BsiqActionButton variant="gold"
                onClick={openCalendly}
                data-testid="final-request-demo-btn"
              >
                Request a demo
              </BsiqActionButton>
              <BsiqActionButton
                variant="inverse"
                onClick={() => document.getElementById('oi')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="final-how-btn"
              >
                See how it works
              </BsiqActionButton>
            </div>
            <p className="text-xs mt-5" style={{ color: '#94A3B8' }}>See how BidsignalIQ works across your actual NAICS codes, set-asides, and operating model.</p>
          </div>
        </section>

        {/* ============ SECTION 10 — FOOTER ============ */}
        <SiteFooter />
      </main>
    </div>
  );
};

export default HomePage;
